import { useCallback, useMemo } from 'react'
import { AlertTriangle, MapPin, Navigation, Radio } from 'lucide-react'
import type { Service } from '@/api/orderDetails'
import { useLocalization } from '@/contexts/LocalizationContext'
import { useServiceLiveLocation } from '@/hooks/useLiveTracking'
import { LiveTrackingMap } from '@/components/maps/LiveTrackingMap'
import { haversineMeters, parseServiceCoords, type LatLng } from '@/utils/geo'
import type { Locale } from '@/translations'

interface ServiceLiveTrackingCardProps {
  service: Service
}

/** Server-reported age, so a skewed client clock cannot produce "in 3 seconds". */
function formatAge(ageSeconds: number, locale: Locale): string {
  const rtf = new Intl.RelativeTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
    numeric: 'auto',
  })
  const seconds = Math.max(0, Math.round(ageSeconds))
  if (seconds < 60) return rtf.format(-seconds, 'second')
  if (seconds < 3600) return rtf.format(-Math.round(seconds / 60), 'minute')
  return rtf.format(-Math.round(seconds / 3600), 'hour')
}

function formatDistance(meters: number, locale: Locale): string {
  const tag = locale === 'ar' ? 'ar-SA' : 'en-US'
  if (meters < 1000) {
    return `${new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }).format(meters)} m`
  }
  return `${new Intl.NumberFormat(tag, { maximumFractionDigits: 1 }).format(meters / 1000)} km`
}

function coordString(point: LatLng): string {
  return `${point.lat},${point.lng}`
}

/**
 * Live map for one in-progress, tracking-enabled service.
 *
 * Renders only while the caller says the tracking window is open — the card unmounting is
 * what stops its polling. A `null` position inside an open window is normal (the provider
 * has not pinged yet, or the viewer is not permitted to see it), so the card stays and
 * says it is waiting rather than disappearing.
 */
export function ServiceLiveTrackingCard({ service }: ServiceLiveTrackingCardProps) {
  const { t, locale } = useLocalization()
  const { location, isLoading } = useServiceLiveLocation(service.id)

  const destination = useMemo(() => parseServiceCoords(service), [service])

  const provider = useMemo<LatLng | null>(
    () => (location ? { lat: location.latitude, lng: location.longitude } : null),
    [location]
  )

  const distanceMeters = useMemo(
    () => (provider && destination ? haversineMeters(provider, destination) : null),
    [provider, destination]
  )

  /**
   * Built at click time rather than rendered as an `href`: coordinates sitting in the DOM
   * get picked up by Mixpanel autocapture and Sentry breadcrumbs, both active here.
   */
  const openInMaps = useCallback(() => {
    const destinationParam = destination
      ? coordString(destination)
      : (service.locationAddress ?? '')

    let url: string

    if (provider && destinationParam) {
      url =
        'https://www.google.com/maps/dir/?api=1' +
        `&origin=${encodeURIComponent(coordString(provider))}` +
        `&destination=${encodeURIComponent(destinationParam)}` +
        '&travelmode=driving'
    } else if (destination) {
      // No provider position: a plain destination pin. `dir` with only a destination
      // would make Maps infer the origin from *the viewer's* location, not the provider's.
      url = `https://www.google.com/maps?q=${encodeURIComponent(coordString(destination))}`
    } else if (service.locationGoogleMapsLink) {
      url = service.locationGoogleMapsLink
    } else if (destinationParam) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destinationParam)}`
    } else {
      return
    }

    window.open(url, '_blank', 'noopener,noreferrer')
  }, [provider, destination, service.locationAddress, service.locationGoogleMapsLink])

  const hasMapsTarget = !!(provider || destination || service.locationGoogleMapsLink || service.locationAddress)
  const isStale = location?.isStale ?? false
  const providerName = location?.providerName || service.providerName

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600">
          <Radio className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-gray-900">{t('liveTracking.title')}</h3>
            {isStale ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                <AlertTriangle className="h-3 w-3" />
                {t('liveTracking.notReporting')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-600" />
                </span>
                {t('liveTracking.live')}
              </span>
            )}
          </div>
          <p className="truncate text-xs text-gray-500">
            {providerName
              ? t('liveTracking.subtitleWithProvider', {
                  service: service.serviceName,
                  provider: providerName,
                })
              : service.serviceName}
          </p>
        </div>
      </div>

      <div className="space-y-3 p-5">
        <LiveTrackingMap
          provider={provider}
          destination={destination}
          headingDegrees={location?.headingDegrees ?? null}
          accuracyMeters={location?.accuracyMeters ?? null}
          isStale={isStale}
          labels={{
            loadFailed: t('liveTracking.mapLoadFailed'),
            authFailed: t('liveTracking.mapAuthFailed'),
            retry: t('common.retry'),
            fit: t('liveTracking.fitBoth'),
          }}
          fallback={
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <span>{t('liveTracking.mapUnavailable')}</span>
            </div>
          }
        />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {location ? (
            <>
              <span className={isStale ? 'text-gray-500' : 'text-gray-700'}>
                {isStale
                  ? t('liveTracking.lastSeen', { time: formatAge(location.ageSeconds, locale) })
                  : t('liveTracking.updated', { time: formatAge(location.ageSeconds, locale) })}
              </span>
              {distanceMeters != null && (
                <span className="text-gray-500">
                  {t('liveTracking.distanceAway', {
                    distance: formatDistance(distanceMeters, locale),
                  })}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-500">
              {isLoading ? t('common.loading') : t('liveTracking.awaitingPosition')}
            </span>
          )}
        </div>

        {isStale && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
            {t('liveTracking.staleWarning')}
          </p>
        )}

        {hasMapsTarget && (
          <button
            type="button"
            onClick={openInMaps}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-brand-primary hover:text-brand-primary"
          >
            <Navigation className="h-4 w-4" />
            {provider ? t('liveTracking.directionsInMaps') : t('liveTracking.openInMaps')}
          </button>
        )}
      </div>
    </div>
  )
}
